import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {Breadcrumb, Input, message} from "antd";
import {DashboardWrapper} from "../../../../../layouts/dashboard-wrapper";
import {StyledPageHeader} from "../../../../../styles/dashboard/calls/calls";
import {
  useConnectMetaPixel,
  useDisconnectMetaPixel,
  useGetMetaPixelStatus,
} from "../../../../../react-query-hooks/integrations/meta-pixel";
import {
  StyledAutomationsPageContent,
  StyledIntegrationConnectButton,
  StyledIntegrationConnectPanel,
  StyledIntegrationDetailCard,
  StyledIntegrationDetailHeader,
  StyledIntegrationDetailLayout,
} from "../../automations.style";
import {FacebookPixelIcon} from "../../icons/facebook-pixel-icon";
import {IntegrationConnectionVisual} from "../components/IntegrationConnectionVisual";

const BULLET_POINTS = [
  "Public profile views",
  "Initiate checkout events",
  "Successful purchase",
];

export function FacebookPixelIntegrationPage() {
  const {data: status} = useGetMetaPixelStatus();
  const connectMutation = useConnectMetaPixel();
  const disconnectMutation = useDisconnectMetaPixel();

  const isConnected = status?.metaPixelConnected ?? false;
  const [pixelId, setPixelId] = useState("");
  const [conversionsApiToken, setConversionsApiToken] = useState("");

  useEffect(() => {
    if (status?.pixelId) setPixelId(status.pixelId);
    if (status?.conversionsApiToken) setConversionsApiToken(status.conversionsApiToken);
  }, [status?.pixelId, status?.conversionsApiToken]);

  function handleConnect() {
    if (!pixelId.trim()) {
      message.warning("Please enter your Facebook (Meta) pixel ID.");
      return;
    }

    if (!conversionsApiToken.trim()) {
      message.warning("Please enter your Conversions API access token.");
      return;
    }

    connectMutation.mutate(
      {
        pixelId: pixelId.trim(),
        conversionsApiToken: conversionsApiToken.trim(),
      },
      {
        onSuccess() {
          message.success("Facebook (Meta) pixel connected successfully!");
        },
        onError(err) {
          message.error(err.message ?? "Failed to connect Facebook (Meta) pixel.");
        },
      }
    );
  }

  function handleDisconnect() {
    disconnectMutation.mutate(undefined, {
      onSuccess() {
        setPixelId("");
        setConversionsApiToken("");
        message.success("Facebook (Meta) pixel disconnected successfully!");
      },
      onError(err) {
        message.error(err.message ?? "Failed to disconnect Facebook (Meta) pixel.");
      },
    });
  }

  return (
    <DashboardWrapper pageTitle="Facebook (Meta) pixel">
      <StyledPageHeader>Automations</StyledPageHeader>
      <StyledAutomationsPageContent>
        <StyledIntegrationDetailHeader>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/automations?tab=integrations">Integrations</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Facebook (Meta) pixel</Breadcrumb.Item>
          </Breadcrumb>
        </StyledIntegrationDetailHeader>

        <StyledIntegrationDetailCard>
          <StyledIntegrationDetailLayout>
            <IntegrationConnectionVisual integrationIcon={<FacebookPixelIcon />} />
            <StyledIntegrationConnectPanel>
              <h2>Once you connect, you will be able to track:</h2>
              <ul>
                {BULLET_POINTS.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>

              <div style={{display: "flex", flexDirection: "column", gap: "1.6rem"}}>
                <div style={{display: "flex", flexDirection: "column", gap: "0.4rem"}}>
                  <label style={{fontSize: "1.4rem", color: "#1c1726", fontWeight: 500}}>
                    Facebook (Meta) pixel ID
                  </label>
                  <Input
                    value={pixelId}
                    onChange={(e) => setPixelId(e.target.value)}
                    placeholder="e.g., C8FD2KL7890123456"
                    disabled={isConnected}
                    style={{
                      borderRadius: "0.8rem",
                      padding: "0.8rem 1.2rem",
                      fontSize: "1.4rem",
                    }}
                  />
                  <span style={{fontSize: "1.2rem", color: "#8c8c8c"}}>
                    Need help finding your Pixel ID?{" "}
                    <a
                      href="https://web.facebook.com/business/help/952192354843755?id=1205376682832142&_rdc=1&_rdr"
                      target="_blank"
                      rel="noreferrer"
                      style={{color: "#1c1726", textDecoration: "underline"}}
                    >
                      Learn how
                    </a>
                  </span>
                </div>

                <div style={{display: "flex", flexDirection: "column", gap: "0.4rem"}}>
                  <label style={{fontSize: "1.4rem", color: "#1c1726", fontWeight: 500}}>
                    Conversions API access token
                  </label>
                  <Input
                    value={conversionsApiToken}
                    onChange={(e) => setConversionsApiToken(e.target.value)}
                    placeholder="e.g., a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6"
                    disabled={isConnected}
                    style={{
                      borderRadius: "0.8rem",
                      padding: "0.8rem 1.2rem",
                      fontSize: "1.4rem",
                    }}
                  />
                  <span style={{fontSize: "1.2rem", color: "#8c8c8c"}}>
                    Events API offers better & effective campaign tracking.{" "}
                    <a
                      href="https://developers.facebook.com/docs/marketing-api/conversions-api"
                      target="_blank"
                      rel="noreferrer"
                      style={{color: "#1c1726", textDecoration: "underline"}}
                    >
                      Learn more
                    </a>
                  </span>
                </div>
              </div>

              {isConnected ? (
                <StyledIntegrationConnectButton
                  type="default"
                  onClick={handleDisconnect}
                  loading={disconnectMutation.isLoading}
                >
                  Disconnect Facebook (Meta) pixel
                </StyledIntegrationConnectButton>
              ) : (
                <StyledIntegrationConnectButton
                  type="primary"
                  onClick={handleConnect}
                  loading={connectMutation.isLoading}
                >
                  Connect Facebook (Meta) pixel
                </StyledIntegrationConnectButton>
              )}
            </StyledIntegrationConnectPanel>
          </StyledIntegrationDetailLayout>
        </StyledIntegrationDetailCard>
      </StyledAutomationsPageContent>
    </DashboardWrapper>
  );
}
